import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Dropdown,
  Field,
  makeStyles,
  mergeClasses,
  Option,
  SearchBox,
  Skeleton,
  SkeletonItem,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import {
  BoxMultipleRegular,
  BuildingShopRegular,
  DismissRegular,
  EditRegular,
  ImageOffRegular,
  MoneyRegular,
  TagMultipleRegular,
  TreeDeciduousRegular,
} from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  catalogue,
  fetchProtectedImage,
  type StockGroup,
  type StockRow,
  type StockSort,
} from "../api/catalogue";
import { commerce } from "../api/commerce";
import { EditPiecePage } from "../catalogue/EditPiecePage";
import { baeTypography } from "../theme/baeTheme";
import { EmptyState, Page, PageHeader, Toolbar } from "../ui/PageHeader";
import { Meter, StatRow, StatTile } from "../ui/StatTile";
import { Card, CardHeader } from "../ui/Surface";
import { elevation, motion, PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  breakdowns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(19rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "start",
  },
  groupList: { display: "grid", gap: "2px", minWidth: 0 },
  // A button, not a div: tapping one filters the page, so it has to be reachable by keyboard and
  // announced as an action.
  group: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
    width: "100%",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    minWidth: 0,
    transitionProperty: "background-color",
    transitionDuration: motion.fast,
    ":hover": { backgroundColor: tokens.colorNeutralBackground3 },
  },
  groupOn: {
    backgroundColor: tokens.colorBrandBackground2,
    ":hover": { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  groupLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  groupFigures: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "baseline",
    flexShrink: 0,
  },
  count: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  meterCell: { paddingInline: tokens.spacingHorizontalS, paddingBottom: "3px" },

  // A garment retailer's stock list is a wall of pictures. 13rem cards give the photo enough room
  // to be recognised across a counter, which a 72px thumbnail never did.
  //
  // Two per row on a phone, stated rather than left to auto-fill. A 4:5 frame in a full-width
  // column is one card per screen — and for a piece with no photo yet, one screen of empty grey.
  // `minmax(0, 1fr)` rather than `1fr` so a long name cannot push the track wider than half.
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(13rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    [PHONE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: tokens.spacingHorizontalS },
  },
  card: {
    display: "grid",
    gridTemplateRows: "auto 1fr",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: elevation.restLight,
    overflow: "hidden",
    minWidth: 0,
    transitionProperty: "box-shadow, transform",
    transitionDuration: motion.base,
    ":hover": { boxShadow: elevation.hoverLight, transform: "translateY(-2px)" },
  },
  frame: {
    position: "relative",
    // Taller than wide, because a garment is. A square crop cuts the hem off everything.
    aspectRatio: "4 / 5",
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: "hidden",
  },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  noImage: {
    display: "grid",
    placeItems: "center",
    width: "100%",
    height: "100%",
    color: tokens.colorNeutralForeground3,
    fontSize: "28px",
  },
  overlay: {
    position: "absolute",
    inset: "auto 0 0 0",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingHorizontalXS,
    background: "linear-gradient(to top, rgba(9,23,37,0.72), transparent)",
  },
  // Sits on the photo, so the number is readable before anything is read.
  onHand: {
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#FFFFFF",
    fontVariantNumeric: "tabular-nums",
    textShadow: "0 1px 2px rgba(0,0,0,0.4)",
  },
  onHandUnit: { fontSize: "0.7rem", fontWeight: 500, opacity: 0.85 },
  editButton: { color: "#FFFFFF", minWidth: "unset" },
  body: {
    display: "grid",
    gap: "2px",
    padding: tokens.spacingHorizontalM,
    alignContent: "start",
    minWidth: 0,
  },
  // Two lines then ellipsis. In a half-width card a single clamped line loses most of the name,
  // and "Indigo & ivory Chikan…" is not enough to tell two designs apart.
  name: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.3,
  },
  oneLine: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  variant: { color: tokens.colorNeutralForeground2 },
  sku: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    // A SKU has no spaces to break at, and at half a phone's width it is wider than its card.
    overflowWrap: "anywhere",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: "3px", marginTop: "3px" },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  active: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    alignItems: "center",
  },
  chipButton: { minWidth: "unset" },
  listHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  skeletonCard: { display: "grid", gap: tokens.spacingVerticalS },
  skeletonFrame: { aspectRatio: "4 / 5", borderRadius: tokens.borderRadiusLarge },
  searchWide: { [PHONE]: { gridColumn: "1 / -1" } },
});

const SORTS: { value: StockSort; label: string }[] = [
  { value: "newest", label: "Recently added" },
  { value: "oldest", label: "Oldest first" },
  { value: "quantity", label: "Most pieces" },
  { value: "value", label: "Highest value" },
  { value: "name", label: "By name" },
];

const money = (value: number | null) =>
  value === null ? "—" : `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const whole = (value: number) => value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

/** Loads a protected photo and revokes its object URL when it goes away. */
function Photo({ path, alt }: { path: string | null; alt: string }) {
  const styles = useStyles();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    void fetchProtectedImage(path).then((result) => {
      if (cancelled) {
        if (result) URL.revokeObjectURL(result);
        return;
      }
      objectUrl = result;
      setUrl(result);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!path) {
    return (
      <div className={styles.noImage}>
        <ImageOffRegular />
      </div>
    );
  }

  return url ? <img src={url} alt={alt} className={styles.image} /> : <div />;
}

/**
 * What is in stock.
 *
 * Answers the question in the order it gets asked: how much is there altogether, then what kind of
 * thing, then where, then which pieces exactly. The two middle answers are also the filters —
 * tapping "Kurtis" narrows everything below it — because the follow-up to "how much apparel" is
 * almost always "show me".
 *
 * Photo-forward on purpose. This is a garment retailer, and a picture is how the team recognises a
 * piece; a name and a SKU are how they confirm it. The list used to be text with a 72px thumbnail,
 * which had that the wrong way round.
 *
 * Every subtotal, including the roll-up that credits a kurti to Apparel as well as to Kurtis, comes
 * from the API. Nothing here sums anything: a phone re-adding several hundred rows to draw a
 * heading is work in the wrong place, and two clients doing it slightly differently would disagree
 * about the stock figure (ADR-0009).
 */
export function StockOnHandPage() {
  const styles = useStyles();

  const [siteId, setSiteId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState<StockSort>("newest");
  const [editing, setEditing] = useState<string | null>(null);

  // Typing "kurti" is five keystrokes and five round trips without this. 250ms is short enough
  // that the list feels live and long enough that a word costs one request.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const locations = useQuery({ queryKey: ["locations"], queryFn: commerce.locations });
  const categories = useQuery({ queryKey: ["categories"], queryFn: commerce.categories });

  const stock = useQuery({
    queryKey: ["stock", siteId, categoryId, debounced, sort],
    queryFn: () =>
      catalogue.stockOnHand({
        siteId: siteId || undefined,
        categoryId: categoryId || undefined,
        search: debounced || undefined,
        sort,
      }),
  });

  const sites = useMemo(
    () => (locations.data ?? []).filter((l) => l.isSite && l.isActive),
    [locations.data],
  );

  if (editing) {
    return <EditPiecePage productId={editing} onDone={() => setEditing(null)} />;
  }

  const rows = stock.data?.rows ?? [];
  const byCategory = stock.data?.byCategory ?? [];
  const bySite = stock.data?.bySite ?? [];

  const siteName = sites.find((s) => s.id === siteId)?.name;
  const categoryName = (categories.data ?? []).find((c) => c.id === categoryId)?.path.join(" / ");
  const filtered = Boolean(siteId || categoryId || debounced);

  // The biggest group sets the scale for every bar, so the bars compare groups against each other
  // rather than against the page total — which is what "where is most of it" actually asks.
  const largestCategory = Math.max(1, ...byCategory.map((g) => g.units));
  const largestSite = Math.max(1, ...bySite.map((g) => g.units));

  return (
    <Page>
      <PageHeader
        title="Stock on hand"
        subtitle="Everything the business is holding, newest first. Tap a category or a store to narrow it."
      />

      <StatRow>
        <StatTile
          icon={<BoxMultipleRegular />}
          label="Pieces on hand"
          value={whole(stock.data?.totalUnits ?? 0)}
          tone="brand"
        />
        <StatTile
          icon={<MoneyRegular />}
          label="Value at cost"
          value={money(stock.data?.totalValue ?? 0)}
          tone="success"
        />
        <StatTile
          icon={<TagMultipleRegular />}
          label="Different SKUs"
          value={stock.data?.distinctSkus ?? 0}
          tone="neutral"
        />
      </StatRow>

      <Toolbar>
        <Field label="Search" className={styles.searchWide}>
          <SearchBox
            value={search}
            placeholder="Name, SKU, code or barcode"
            onChange={(_, d) => setSearch(d.value)}
          />
        </Field>

        <Field label="Store">
          <Dropdown
            value={siteName ?? "Everywhere"}
            selectedOptions={siteId ? [siteId] : ["all"]}
            onOptionSelect={(_, data) =>
              setSiteId(data.optionValue === "all" ? "" : (data.optionValue ?? ""))
            }
          >
            <Option value="all" text="Everywhere">
              Everywhere
            </Option>
            {sites.map((site) => (
              <Option key={site.id} value={site.id} text={site.name}>
                {site.name}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Category">
          <Dropdown
            value={categoryName ?? "Everything"}
            selectedOptions={categoryId ? [categoryId] : ["all"]}
            onOptionSelect={(_, data) =>
              setCategoryId(data.optionValue === "all" ? "" : (data.optionValue ?? ""))
            }
          >
            <Option value="all" text="Everything">
              Everything
            </Option>
            {(categories.data ?? []).map((category) => (
              <Option key={category.id} value={category.id} text={category.path.join(" / ")}>
                {category.path.join(" / ")}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Order">
          <Dropdown
            value={SORTS.find((s) => s.value === sort)?.label ?? "Recently added"}
            selectedOptions={[sort]}
            onOptionSelect={(_, data) => setSort((data.optionValue as StockSort) ?? "newest")}
          >
            {SORTS.map((option) => (
              <Option key={option.value} value={option.value} text={option.label}>
                {option.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </Toolbar>

      {filtered && (
        <div className={styles.active}>
          <Caption1 className={styles.muted}>Showing</Caption1>

          {categoryName && (
            <Button
              size="small"
              appearance="primary"
              className={styles.chipButton}
              icon={<DismissRegular />}
              iconPosition="after"
              onClick={() => setCategoryId("")}
            >
              {categoryName}
            </Button>
          )}
          {siteName && (
            <Button
              size="small"
              appearance="primary"
              className={styles.chipButton}
              icon={<DismissRegular />}
              iconPosition="after"
              onClick={() => setSiteId("")}
            >
              {siteName}
            </Button>
          )}
          {debounced && (
            <Button
              size="small"
              appearance="secondary"
              className={styles.chipButton}
              icon={<DismissRegular />}
              iconPosition="after"
              onClick={() => {
                setSearch("");
                setDebounced("");
              }}
            >
              “{debounced}”
            </Button>
          )}

          <Button
            size="small"
            appearance="subtle"
            onClick={() => {
              setSiteId("");
              setCategoryId("");
              setSearch("");
              setDebounced("");
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {stock.isLoading ? (
        <div className={styles.list}>
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <Skeleton key={n} className={styles.skeletonCard}>
              <SkeletonItem className={styles.skeletonFrame} />
              <SkeletonItem size={16} />
              <SkeletonItem size={12} />
            </Skeleton>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BoxMultipleRegular />}
            title={filtered ? "Nothing matches that" : "Nothing in stock yet"}
            action={
              filtered ? (
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setSiteId("");
                    setCategoryId("");
                    setSearch("");
                    setDebounced("");
                  }}
                >
                  Clear the filters
                </Button>
              ) : undefined
            }
          >
            {filtered
              ? "Search looks at names, SKUs, product codes and barcodes across the whole catalogue — so try a shorter word, or clear a filter."
              : "Add a piece, or receive a delivery in Goods in."}
          </EmptyState>
        </Card>
      ) : (
        <>
          <div className={styles.breakdowns}>
            <Card>
              <CardHeader
                icon={<TreeDeciduousRegular />}
                title="What you have"
                hint="A category counts everything beneath it. Tap one to see just those pieces."
              />

              <div className={styles.groupList}>
                {byCategory.map((group) => (
                  <GroupRow
                    key={group.id}
                    group={group}
                    largest={largestCategory}
                    selected={categoryId === group.id}
                    onSelect={() => setCategoryId(categoryId === group.id ? "" : group.id)}
                  />
                ))}
              </div>
            </Card>

            <Card>
              <CardHeader
                icon={<BuildingShopRegular />}
                title="Where it is"
                hint="Busiest store first."
              />

              <div className={styles.groupList}>
                {bySite.map((group) => (
                  <GroupRow
                    key={group.id}
                    group={group}
                    largest={largestSite}
                    selected={siteId === group.id}
                    onSelect={() => setSiteId(siteId === group.id ? "" : group.id)}
                  />
                ))}
              </div>
            </Card>
          </div>

          <div className={styles.listHead}>
            <Body1Strong>{rows.length === 1 ? "1 line" : `${rows.length} lines`}</Body1Strong>
            {stock.isFetching && <Spinner size="tiny" label="Updating" />}
          </div>

          <div className={styles.list}>
            {rows.map((row) => (
              <StockCard
                key={`${row.productVariantId}-${row.siteId}`}
                row={row}
                onEdit={() => setEditing(row.productId)}
              />
            ))}
          </div>

          {rows.length >= 500 && (
            <Caption1 className={styles.muted}>
              Showing the 500 most recently moved lines. Narrow it with a search or a filter to see
              past that.
            </Caption1>
          )}
        </>
      )}
    </Page>
  );
}

function GroupRow({
  group,
  largest,
  selected,
  onSelect,
}: {
  group: StockGroup;
  largest: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const styles = useStyles();

  return (
    <div>
      <button
        type="button"
        aria-pressed={selected}
        className={mergeClasses(styles.group, selected && styles.groupOn)}
        // Indented by the depth the API reported, so the tree is visible without redrawing it.
        style={{ paddingLeft: `${group.depth * 1.1 + 0.5}rem` }}
        title={group.detail ?? group.label}
        onClick={onSelect}
      >
        <span className={styles.groupLabel}>{group.label}</span>
        <span className={styles.groupFigures}>
          <span className={styles.count}>{whole(group.units)}</span>
          <Caption1 className={styles.muted}>{money(group.value)}</Caption1>
        </span>
      </button>
      <div className={styles.meterCell}>
        <Meter value={group.units} max={largest} />
      </div>
    </div>
  );
}

function StockCard({ row, onEdit }: { row: StockRow; onEdit: () => void }) {
  const styles = useStyles();

  return (
    <div className={styles.card}>
      <div className={styles.frame}>
        <Photo path={row.primaryPhotoUrl} alt={row.productName} />

        <div className={styles.overlay}>
          <span className={styles.onHand}>
            {whole(row.available)} <span className={styles.onHandUnit}>available</span>
          </span>
          <Button
            size="small"
            appearance="subtle"
            className={styles.editButton}
            icon={<EditRegular />}
            aria-label={`Edit ${row.productName}`}
            onClick={onEdit}
          />
        </div>
      </div>

      <div className={styles.body}>
        <Body1Strong className={styles.name} title={row.productName}>
          {row.productName}
        </Body1Strong>

        {/* The variant, when there is one, is what tells two cards of the same design apart. */}
        {row.optionSummary && (
          <Caption1 className={`${styles.oneLine} ${styles.variant}`}>{row.optionSummary}</Caption1>
        )}

        <span className={styles.sku}>{row.sku}</span>

        {/* Only shown when non-zero, so an ordinary card stays quiet and anything unusual stands out. */}
        {(row.reserved > 0 || row.damaged > 0 || row.quarantine > 0 || row.inTransit > 0) && (
          <div className={styles.chips}>
            {row.reserved > 0 && (
              <Badge appearance="tint" color="informative" size="small">
                {whole(row.reserved)} reserved
              </Badge>
            )}
            {row.damaged > 0 && (
              <Badge appearance="tint" color="danger" size="small">
                {whole(row.damaged)} damaged
              </Badge>
            )}
            {row.quarantine > 0 && (
              <Badge appearance="tint" color="warning" size="small">
                {whole(row.quarantine)} on hold
              </Badge>
            )}
            {row.inTransit > 0 && (
              <Badge appearance="outline" size="small">
                {whole(row.inTransit)} in transit
              </Badge>
            )}
          </div>
        )}

        <div className={styles.footer}>
          <Caption1 className={styles.muted} title={row.categoryPath.join(" / ")}>
            {row.siteCode}
          </Caption1>
          <Caption1 className={styles.muted}>{money(row.averageCost)} each</Caption1>
        </div>
      </div>
    </div>
  );
}
